import {
  Badge,
  Body,
  Group,
  H6,
  MaterialIcon,
  Stack,
} from '@qred/components-library';
import React, { Fragment } from 'react';
import useTranslate from '~/hooks/useTranslate';
import useStepConfig from '../../Steps/useStepConfig';
import {
  Bullet,
  Container,
  IconWrapper,
  ItemWrapper,
  Line,
} from './VerticalTimeline.styled';

export const VerticalTimeline = () => {
  const {
    turnoverQuestionIsAfterCheckpoint,
    applicationCanHaveGuarantors,
  } = useStepConfig();
  const t = useTranslate();
  const completedIndex = 1;

  const items: React.JSX.Element[] = [
    <Stack key="basic_application_sent" spacing="sm" mt={3}>
      <H6 data-record>{t('Onboarding.CheckpointBasicApplicationSentStep')}</H6>
      <Body size="lg" mb="xxl" data-record>
        {t('Onboarding.CheckpointBasicApplicationSentStepDescription')}
      </Body>
    </Stack>,
    ...(applicationCanHaveGuarantors
      ? [
          <Group
            key="guarantors_step"
            align="center"
            justify="space-between"
            w="100%"
            nowrap
          >
            <Body align="left" data-record>
              {t('Onboarding.CheckpointGuarantorsStep')}
            </Body>
            <Badge color="Main/Grey/300" size="sm" data-record>
              {t('Onboarding.CheckpointIncompleteStep')}
            </Badge>
          </Group>,
        ]
      : []),
    <Group
      key="turnover_steps"
      align="center"
      justify="space-between"
      w="100%"
      pb={40}
    >
      <Body align="left" data-record>
        {turnoverQuestionIsAfterCheckpoint
          ? t('Onboarding.CheckpointTurnoverStep')
          : t('Onboarding.CheckpointVerifyTurnoverStep')}
      </Body>
      <Badge color="Main/Grey/300" size="sm" data-record>
        {t('Onboarding.CheckpointIncompleteStep')}
      </Badge>
    </Group>,

    <Stack key="boosted_application" mt={3}>
      <H6 data-record>{t('Onboarding.CheckpointBoostedApplicationStep')}</H6>
      <Body size="lg" data-record>
        {t('Onboarding.CheckpointBoostedApplicationStepDescription')}
      </Body>
    </Stack>,
  ];

  return (
    <Body size="lg">
      <Container data-testid="checkpoint">
        {items.map((item, index) => {
          const isLastItem = index === items.length - 1;
          const isCompleted = index < completedIndex;

          return (
            <Fragment key={item.key}>
              <IconWrapper>
                {getIcon(isLastItem, isCompleted)}
                {!isLastItem && <Line $isCompleted={isCompleted} />}
              </IconWrapper>
              <ItemWrapper>{item}</ItemWrapper>
            </Fragment>
          );
        })}
      </Container>
    </Body>
  );
};

const getIcon = (
  isLastItem: boolean,
  isCompleted: boolean
): React.JSX.Element => {
  if (isLastItem) {
    return (
      <MaterialIcon
        src="new_releases"
        color={isCompleted ? 'Main/Primary/800' : 'Main/Grey/800'}
        size={24}
      />
    );
  }

  if (isCompleted) {
    return (
      <MaterialIcon
        src="check_circle"
        color="Main/Primary/800"
        filled
        size={24}
      />
    );
  }

  return <Bullet bg="Text/Secondary" />;
};
